import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X, Trash2, Eye, AlertTriangle, Folder, FolderPlus, ArrowLeft, Edit2, Check, Sparkles, Share2, Users, HelpCircle } from 'lucide-react';
import { useDosar } from '../context/DosarContext.shared';
import { useAuth } from '../context/AuthContext.shared';
import { useManual } from './manual/ManualContext.shared';
import type { CaseData, Folder as FolderType } from '../context/DosarContext.shared';
import { ShareFolderModal } from './ShareFolderModal'; // Import share modal

const CaseDetailModal = React.lazy(() => import('./CaseDetailModal'));
const PromptGeneratorModal = React.lazy(() => import('./PromptGeneratorModal'));



const DosarDrawer: React.FC = () => {
    const {
        isDrawerOpen, toggleDrawer,
        items, removeFromDosar,
        folders, foldersSharedWithMe, loadFolders, createNewFolder, updateFolder, removeFolder,
        currentFolderId, openFolder, showToast
    } = useDosar();
    const { isAuthenticated } = useAuth();
    const [selectedCase, setSelectedCase] = useState<CaseData | null>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    // Prompt Modal State
    const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);

    // Sharing State
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [folderToShare, setFolderToShare] = useState<{ id: number; nume: string } | null>(null);

    // Folder Management State
    const [viewMode, setViewMode] = useState<'folders' | 'items'>('folders');
    const [newFolderName, setNewFolderName] = useState('');
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [editingFolderId, setEditingFolderId] = useState<number | null>(null);
    const [editFolderName, setEditFolderName] = useState('');
    const [folderToDelete, setFolderToDelete] = useState<number | null>(null);

    // Case Selection State
    const [selectedCaseIds, setSelectedCaseIds] = useState<Set<number | string>>(new Set());

    useEffect(() => {
        // Auto-select first 5 items when items change or folder is opened
        if (items.length > 0) {
            const first5Ids = items.slice(0, 5).map(i => i.id);
            setSelectedCaseIds(new Set(first5Ids));
        } else {
            setSelectedCaseIds(new Set());
        }
    }, [items, currentFolderId]);

    const handleToggleSelection = (id: number | string) => {
        const newSelected = new Set(selectedCaseIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            if (newSelected.size >= 5) {
                showToast("Puteți selecta maxim 5 spețe pentru generarea promptului.", "info");
                return;
            }
            newSelected.add(id);
        }
        setSelectedCaseIds(newSelected);
    };

    useEffect(() => {
        if (isDrawerOpen && isAuthenticated) {
            loadFolders();
        }
    }, [isDrawerOpen, isAuthenticated, loadFolders]);

    // Manual Context Integration
    const { setManualStepId } = useManual();
    useEffect(() => {
        if (isDrawerOpen) {
            setManualStepId(6); // Step 6: Dosare Personale
        } else {
            setManualStepId(null);
        }
        return () => setManualStepId(null);
    }, [isDrawerOpen, setManualStepId]);

    useEffect(() => {
        // If currentFolderId changes, switch view
        if (currentFolderId === null) {
            setViewMode('folders');
        } else {
            setViewMode('items');
        }
    }, [currentFolderId]);

    const handleViewCase = (item: CaseData) => {
        // Toggle drawer closed when viewing details
        if (isDrawerOpen) toggleDrawer();
        setSelectedCase(item);
        setIsDetailOpen(true);
    };

    const handleCloseDetail = () => {
        setIsDetailOpen(false);
        setSelectedCase(null);
    };

    const handleCreateFolder = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newFolderName.trim()) return;
        await createNewFolder(newFolderName);
        setNewFolderName('');
        setIsCreatingFolder(false);
    };

    const handleUpdateFolder = async (id: number) => {
        if (!editFolderName.trim()) return;
        await updateFolder(id, { nume: editFolderName });
        setEditingFolderId(null);
    }

    const startEditing = (folder: FolderType) => {
        setEditingFolderId(folder.id);
        setEditFolderName(folder.nume);
    }

    const openShareModal = (folder: FolderType) => {
        setFolderToShare({ id: folder.id, nume: folder.nume });
        setIsShareModalOpen(true);
    };

    // Find current folder in personal or shared lists
    const currentFolder = folders.find(f => f.id === currentFolderId) || foldersSharedWithMe.find(f => f.id === currentFolderId);

    // Local state fallback for non-auth users
    useEffect(() => {
        if (currentFolderId === null) {
            if (isAuthenticated) {
                setViewMode('folders');
            } else {
                setViewMode('items');
            }
        } else {
            setViewMode('items');
        }
    }, [currentFolderId, isAuthenticated]);

    return (
        <>
            <Transition show={isDrawerOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[110]" onClose={toggleDrawer}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-in-out duration-500"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-500"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-hidden">
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full md:w-auto max-w-full pl-0 md:pl-10">
                                <Transition.Child
                                    as={Fragment}
                                    enter="transform transition ease-in-out duration-500 sm:duration-700"
                                    enterFrom="translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transform transition ease-in-out duration-500 sm:duration-700"
                                    leaveFrom="translate-x-0"
                                    leaveTo="translate-x-full"
                                >
                                    <Dialog.Panel className="pointer-events-auto w-full md:w-screen md:max-w-md">
                                        <div className="flex h-full flex-col overflow-y-scroll bg-surface shadow-raised">
                                            {/* Header */}
                                            <div className="px-4 py-6 sm:px-6 bg-brand-dark text-white">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-2">
                                                        {viewMode === 'items' && (
                                                            <button
                                                                onClick={() => openFolder(null)}
                                                                className="mr-2 hover:bg-surface/10 p-1 rounded transition"
                                                            >
                                                                <ArrowLeft size={20} />
                                                            </button>
                                                        )}
                                                        <Dialog.Title className="text-lg font-medium">
                                                            {viewMode === 'folders' ? 'Dosare' : (
                                                                <span className="flex items-center gap-2">
                                                                    {currentFolder?.nume || (isAuthenticated ? 'Dosarul Meu' : 'Dosar Temporar')}
                                                                    {currentFolder?.is_shared && (
                                                                        <span className="text-xs bg-purple-500/20 px-2 py-0.5 rounded border border-ai/30 text-ai">
                                                                            Partajat
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            )}
                                                            {viewMode === 'items' && <span className="text-sm font-normal ml-2 opacity-80">({items.length}/10)</span>}
                                                        </Dialog.Title>
                                                    </div>
                                                    <div className="ml-3 flex h-7 items-center gap-2">
                                                        {viewMode === 'items' && currentFolder && (!currentFolder.is_shared || currentFolder.permission === 'write') && (
                                                            <button
                                                                type="button"
                                                                className="rounded-md bg-brand-dark text-content-muted hover:text-white focus:outline-none p-1 hover:bg-surface/10"
                                                                onClick={() => openShareModal(currentFolder)}
                                                                title="Partajează Dosarul"
                                                            >
                                                                <Share2 className="h-5 w-5" />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="rounded-md bg-brand-dark text-info hover:text-white focus:outline-none p-1 hover:bg-surface/10"
                                                            onClick={useManual().openManual}
                                                            title="Ghid Dosare"
                                                        >
                                                            <HelpCircle className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="rounded-md bg-brand-dark text-content-muted hover:text-white focus:outline-none"
                                                            onClick={toggleDrawer}
                                                        >
                                                            <span className="sr-only">Close panel</span>
                                                            <X className="h-6 w-6" aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                </div>
                                                {!isAuthenticated && (
                                                    <div className="mt-4 flex items-center text-sm text-warning bg-yellow-900/30 p-2 rounded border border-warning/30">
                                                        <AlertTriangle size={16} className="mr-2 flex-shrink-0" />
                                                        <p>Atenție: Dosarul se golește la părăsirea paginii. Autentifică-te pentru a salva permanent.</p>
                                                    </div>
                                                )}

                                                {/* Generate Prompt Button - Only visible in Folder View when items exist */}
                                                {/* Generate Prompt Button - Only visible in Folder View when items exist */}
                                                {viewMode === 'items' && isAuthenticated && items.length > 0 && currentFolderId !== null && (
                                                    <div className="mt-4">
                                                        <button
                                                            onClick={() => setIsPromptModalOpen(true)}
                                                            disabled={selectedCaseIds.size === 0}
                                                            className={`w-full flex items-center justify-center gap-2 bg-gradient-to-r from-brand-primary to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white p-2.5 rounded-lg shadow-raised shadow-blue-900/20 transition-all font-medium text-sm group border border-white/10 ${selectedCaseIds.size === 0 ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                                                        >
                                                            <Sparkles size={16} className={`${selectedCaseIds.size > 0 ? 'group-hover:animate-pulse' : ''} `} />
                                                            Generare Prompt Juridic ({selectedCaseIds.size}/5)
                                                        </button>
                                                        {selectedCaseIds.size === 0 && (
                                                            <p className="text-xs text-danger text-center mt-1">Selectați cel puțin o speță.</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className="relative mt-6 flex-1 px-4 sm:px-6">

                                                {/* FOLDERS VIEW */}
                                                {viewMode === 'folders' && isAuthenticated && (
                                                    <div className="space-y-4">
                                                        {/* Create New Folder */}
                                                        {!isCreatingFolder ? (
                                                            <button
                                                                onClick={() => setIsCreatingFolder(true)}
                                                                className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-border-strong rounded-lg text-content-muted hover:border-brand-primary hover:text-brand-primary transition-all group"
                                                            >
                                                                <FolderPlus className="group-hover:scale-110 transition-transform" />
                                                                <span>Creează dosar nou</span>
                                                            </button>
                                                        ) : (
                                                            <form onSubmit={handleCreateFolder} className="bg-background p-3 rounded-lg border border-brand-primary/30">
                                                                <input
                                                                    type="text"
                                                                    autoFocus
                                                                    placeholder="Nume dosar..."
                                                                    className="w-full px-3 py-2 border rounded mb-2 text-sm"
                                                                    value={newFolderName}
                                                                    onChange={(e) => setNewFolderName(e.target.value)}
                                                                />
                                                                <div className="flex gap-2 justify-end">
                                                                    <button type="button" onClick={() => setIsCreatingFolder(false)} className="text-sm text-content-muted px-3 py-1 hover:bg-gray-200 rounded">Anulează</button>
                                                                    <button type="submit" className="text-sm bg-brand-primary text-white px-3 py-1 rounded hover:bg-brand-dark">Creează</button>
                                                                </div>
                                                            </form>
                                                        )}

                                                        {/* Folders List */}
                                                        <ul className="space-y-2 mt-4">
                                                            {folders.map(folder => (
                                                                <li key={folder.id} className="group flex items-center justify-between p-3 bg-surface border border-border rounded-lg shadow-subtle hover:shadow-card hover:border-brand-primary/50 transition-all cursor-pointer"
                                                                    onClick={() => { if (editingFolderId !== folder.id) openFolder(folder.id) }}
                                                                >
                                                                    {editingFolderId === folder.id ? (
                                                                        <div className="flex-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                                            <input
                                                                                type="text"
                                                                                className="flex-1 border rounded px-2 py-1 text-sm"
                                                                                value={editFolderName}
                                                                                onChange={(e) => setEditFolderName(e.target.value)}
                                                                                autoFocus
                                                                            />
                                                                            <button onClick={() => handleUpdateFolder(folder.id)} className="text-success hover:bg-success/10 p-1 rounded"><Check size={16} /></button>
                                                                            <button onClick={() => setEditingFolderId(null)} className="text-content-muted hover:bg-surface-subtle p-1 rounded"><X size={16} /></button>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <div className="flex items-center gap-3">
                                                                                <Folder className="text-brand-primary fill-brand-primary/10" size={24} />
                                                                                <span className="font-medium text-content-secondary">{folder.nume}</span>
                                                                            </div>
                                                                            <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                                                                <button
                                                                                    onClick={() => openShareModal(folder)}
                                                                                    className="p-1.5 text-content-muted hover:text-info hover:bg-info/10 rounded"
                                                                                    title="Partajează"
                                                                                >
                                                                                    <Share2 size={16} />
                                                                                </button>
                                                                                <button onClick={() => startEditing(folder)} className="p-1.5 text-content-muted hover:text-brand-primary hover:bg-info/10 rounded" title="Redenumește">
                                                                                    <Edit2 size={16} />
                                                                                </button>
                                                                                <button onClick={() => setFolderToDelete(folder.id)} className="p-1.5 text-content-muted hover:text-danger hover:bg-danger/10 rounded" title="Șterge">
                                                                                    <Trash2 size={16} />
                                                                                </button>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>

                                                        {/* Shared Folders Section */}
                                                        {foldersSharedWithMe.length > 0 && (
                                                            <div className="mt-6 pt-4 border-t border-border">
                                                                <h3 className="text-xs font-semibold text-content-muted uppercase tracking-wider mb-3 flex items-center gap-2">
                                                                    <Users className="w-4 h-4" />
                                                                    Partajat cu mine
                                                                </h3>
                                                                <div className="space-y-2">
                                                                    {foldersSharedWithMe.map(folder => (
                                                                        <div
                                                                            key={`shared - ${folder.id} `}
                                                                            className="group flex items-center justify-between p-3 bg-info/10 border border-info/30 rounded-lg hover:shadow-card transition-all cursor-pointer"
                                                                            onClick={() => openFolder(folder.id)}
                                                                        >
                                                                            <div className="flex items-center gap-3 min-w-0">
                                                                                <div className="p-1.5 bg-ai/10 rounded-lg">
                                                                                    <Users className="w-4 h-4 text-ai" />
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <p className="font-medium text-content-secondary text-sm truncate">{folder.nume}</p>
                                                                                    <p className="text-xs text-content-muted truncate">
                                                                                        de la {folder.owner_name}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-surface text-content-secondary border border-border">
                                                                                {folder.permission === 'read' ? 'View' : 'Edit'}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {folders.length === 0 && foldersSharedWithMe.length === 0 && !isCreatingFolder && (
                                                            <div className="text-center py-8 px-2">
                                                                <div className="bg-info/10 rounded-xl p-6 border border-info/30">
                                                                    <div className="mx-auto w-12 h-12 bg-surface rounded-full flex items-center justify-center shadow-subtle mb-4">
                                                                        <Folder className="text-brand-primary" size={24} />
                                                                    </div>
                                                                    <h3 className="text-base font-semibold text-content mb-2">Organizează-ți activitatea juridică</h3>
                                                                    <p className="text-sm text-content-secondary mb-4 leading-relaxed">
                                                                        Creează dosare personalizate pentru a salva și structura spețele relevante pe care le găsești.
                                                                        Îți poți construi propria bibliotecă de jurisprudență pentru fiecare caz în lucru.
                                                                    </p>

                                                                    <div className="bg-surface rounded-lg p-3 text-left text-xs border border-border shadow-subtle mb-4">
                                                                        <span className="font-semibold text-content block mb-1">💡 Exemplu practic:</span>
                                                                        <p className="text-content-muted">
                                                                            Creează un dosar numit <span className="font-medium text-content-secondary">"Litigiu Terenuri Agri"</span> sau <span className="font-medium text-content-secondary">"Divorț Popescu"</span> și salvează aici toate deciziile similare identificate în căutări.
                                                                        </p>
                                                                    </div>

                                                                    <div className="flex justify-center">
                                                                        <button
                                                                            onClick={() => setIsCreatingFolder(true)}
                                                                            className="text-sm font-medium text-brand-primary hover:text-content flex items-center gap-1 transition-colors"
                                                                        >
                                                                            Începe prin a crea primul dosar
                                                                            <ArrowLeft className="rotate-180" size={16} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* ITEMS VIEW */}
                                                {viewMode === 'items' && (
                                                    items.length === 0 ? (
                                                        <div className="text-center text-content-muted mt-10">
                                                            <p>Nu ai nicio speță în acest dosar.</p>
                                                            <p className="text-sm mt-2">Adaugă spețe folosind iconița de dosar din rezultatele căutării.</p>
                                                        </div>
                                                    ) : (
                                                        <ul className="space-y-4">
                                                            {items.map((item) => (
                                                                <li key={item.id} className={`p-4 rounded-lg border transition-all ${selectedCaseIds.has(item.id) ? 'bg-info/10 border-info/30 shadow-card' : 'bg-background border-border shadow-subtle'}`}>
                                                                    <div className="flex gap-3 items-start">
                                                                        {/* Selection Checkbox */}
                                                                        <div className="pt-1">
                                                                            <label className="flex items-center cursor-pointer relative">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="sr-only"
                                                                                    checked={selectedCaseIds.has(item.id)}
                                                                                    onChange={() => {
                                                                                        if (!selectedCaseIds.has(item.id) && selectedCaseIds.size >= 5) {
                                                                                            showToast("Ați atins limita de 5 spețe pentru generare.", "error");
                                                                                        } else {
                                                                                            handleToggleSelection(item.id);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                                <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${selectedCaseIds.has(item.id) ? 'bg-brand-primary border-brand-primary' : 'border-border-strong bg-surface hover:border-brand-primary'}`}>
                                                                                    {selectedCaseIds.has(item.id) && <Check size={12} className="text-white" />}
                                                                                </div>
                                                                            </label>
                                                                        </div>

                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex justify-between items-start">
                                                                                <h4 className="text-sm font-semibold text-content line-clamp-2 mb-2 flex-1 pr-2 cursor-pointer" onClick={() => handleViewCase(item)}>
                                                                                    {item.data.titlu || `Caz #${item.id} `}
                                                                                </h4>
                                                                                {/* Allow deletion if own folder OR if write permission */}
                                                                                {(!currentFolder?.is_shared || currentFolder.permission === 'write') && (
                                                                                    <button
                                                                                        onClick={() => removeFromDosar(item.id)}
                                                                                        className="text-content-muted hover:text-danger transition-colors p-1 flex-shrink-0"
                                                                                        title="Șterge din dosar"
                                                                                    >
                                                                                        <Trash2 size={18} />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex justify-between items-center mt-2">
                                                                                <span className="text-xs text-content-muted bg-gray-200 px-2 py-0.5 rounded truncate max-w-[150px]">
                                                                                    {item.data.materie || 'N/A'}
                                                                                </span>
                                                                                <button
                                                                                    onClick={() => handleViewCase(item)}
                                                                                    className="flex items-center text-xs font-medium text-brand-primary hover:text-content transition-colors flex-shrink-0"
                                                                                >
                                                                                    <Eye size={14} className="mr-1" />
                                                                                    Vezi detalii
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {selectedCase && (
                <React.Suspense fallback={null}>
                    <CaseDetailModal
                        isOpen={isDetailOpen}
                        onClose={handleCloseDetail}
                        result={selectedCase}
                    />
                </React.Suspense>
            )}

            {/* Prompt Generator Modal */}
            <React.Suspense fallback={null}>
                {currentFolderId && (
                    <PromptGeneratorModal
                        isOpen={isPromptModalOpen}
                        onClose={() => setIsPromptModalOpen(false)}
                        folderId={currentFolderId}
                        selectedItems={items.filter(i => selectedCaseIds.has(i.id))}
                    />
                )}
            </React.Suspense>

            {/* Share Folder Modal */}
            {folderToShare && (
                <ShareFolderModal
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    folderId={folderToShare.id}
                    folderName={folderToShare.nume}
                />
            )}

            {/* Custom Delete Confirmation Modal */}
            <Transition appear show={folderToDelete !== null} as={Fragment}>
                <Dialog as="div" className="relative z-[150]" onClose={() => setFolderToDelete(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-xl bg-surface p-6 text-left align-middle shadow-raised transition-all border border-border">
                                    <div className="flex items-center gap-3 mb-4 text-danger">
                                        <div className="p-2 bg-danger/10 rounded-full">
                                            <AlertTriangle size={24} />
                                        </div>
                                        <Dialog.Title as="h3" className="text-lg font-bold text-content leading-tight">
                                            Ștergere Dosar
                                        </Dialog.Title>
                                    </div>

                                    <div className="mt-2 text-sm text-content-secondary">
                                        <p>
                                            Sunteți sigur că doriți să ștergeți acest dosar?
                                        </p>
                                        <p className="mt-2 font-medium bg-danger/10 text-danger p-2 rounded text-xs border border-danger/30">
                                            Partajările vor fi revocate, iar spețele salvate vor fi mutate în dosarul general.
                                        </p>
                                    </div>

                                    <div className="mt-6 flex justify-end gap-3">
                                        <button
                                            type="button"
                                            className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface border border-border-strong rounded-lg hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-focus transition-colors"
                                            onClick={() => setFolderToDelete(null)}
                                        >
                                            Anulează
                                        </button>
                                        <button
                                            type="button"
                                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-focus shadow-subtle transition-colors"
                                            onClick={() => {
                                                if (folderToDelete) removeFolder(folderToDelete);
                                                setFolderToDelete(null);
                                            }}
                                        >
                                            Confirmă Ștergerea
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
};

export default DosarDrawer;
